import { useNavigate, useParams } from "react-router";
import { connect, useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import classNames from "classnames";
import { State } from "redux/store";
import { useEffect, useRef, useState } from "react";

import {
  CLUSTER_DETAILS_PAGE,
  DATASET_CREATION_PAGE,
  DATA_PAGE,
  IMAGE_DETAILS_PAGE,
} from "helpers/constants/pages";
import {
  getAllSelectedFilters,
  getDataset,
  getExportCount,
  getExportDataArray,
  getFilteredSimilarityClusters,
  getIsDataIngestionTabEnabled,
  getIsExportEntitiesEnabled,
  getIsEnrichmentTabEnabled,
  getNavigationCluster,
  getShowCart,
  isCartEnabled,
  isSavedViewsEnabled,
  isSharingEnabled,
  getShowExportModal,
} from "redux/SingleDataset/selectors";
import { getUserInfo } from "redux/User/selectors";
import { getImageDataLoader } from "redux/Modals/selectors";

import DescriptionList from "views/components/DescriptionList";
import ProgressBarLoader from "views/uikit/ProgressBarLoader";
import {
  getCreationStatus,
  getIsLoading,
  getIsNewDatasetReady,
  getNewDataset,
  getNewDatasetStatus,
} from "redux/CreateDataset/selectors";

import { setShowDeleteDatasetModal } from "redux/CreateDataset/actions";
import {
  setShowExportModal,
  setShowSharingMenu,
} from "redux/SingleDataset/actions";
import { copyToClipboard, isOnPremUser } from "helpers/utility/utilities";
import { setEnrichedDatasetId } from "redux/Enrichment/actions";
import { isAnonymousUser, userLogout } from "redux/authReducer";

import DatasetDeletionModal from "views/modals/DatasetDeletionModal";
import EditableDatasetTitle from "../EditableDatasetTitle";
import PublicHeader from "views/components/PublicHeader";
import TooltipWrapper from "views/uikit/TooltipWrapper";
import DatasetStatusComponent from "./DatasetStatus";
import ExportModal from "views/modals/ExportModal";
import SignupModal from "views/modals/SignupModal";
import ActionOptions from "./ActionOptions";
import SelectionBox from "./SelectionBox";
import TabSwitch from "./TabSwitch";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";

import { PRIVATE_DATASETS_PATH } from "helpers/constants/paths";
import { DatasetStatusEnum, TabTypes } from "types";

import CheckIcon from "assets/icons/CheckIcon";
import KebabIcon from "assets/icons/KebabIcon";
import ShareIcon from "assets/icons/ShareIcon";
import ExportIcon from "assets/icons/ExportIcon";

import styles from "./style.module.scss";
import {getVisibleSubtasks} from "../../pages/DatasetCreationPage/DatasetCreationWizard/Steps/CreateDataset";

const OPTIONS_BUTTON_TOOLTIP_ID = "dataset-header-options-button-tooltip";
const EXPORT_BUTTON_TOOLTIP_ID = "dataset-header-export-button-tooltip";
const SHARE_BUTTON_TOOLTIP_ID = "dataset-header-share-button-tooltip";

const PublicHeaderComponent = () => {
  return (
    <>
      <PublicHeader isBig={false} />
      <SignupModal />
    </>
  );
};

const ShowLoader = ({
  dynamicPage,
  isImageLoading,
  isCardListLoading,
}: any) => {
  const isImageDetailsPage = dynamicPage === IMAGE_DETAILS_PAGE;
  const isClusterOrDataPage =
    dynamicPage === CLUSTER_DETAILS_PAGE || dynamicPage === DATA_PAGE;
  const isDatasetCrationPage = dynamicPage === DATASET_CREATION_PAGE;

  if (isImageDetailsPage) {
    return <ProgressBarLoader loading={isImageLoading} />;
  }

  if (isClusterOrDataPage || isDatasetCrationPage) {
    return <ProgressBarLoader loading={isCardListLoading} />;
  }

  return null;
};

const ContainerHeader = ({
  page = DATA_PAGE,
  dynamicPage = DATA_PAGE,
  isContentLoading = false,
  isCardListLoading = false,
  singleImageData,
  dataset,
  exportCount,
  exportData,
  showCart,
  clusters,
  navigationCluster,
  cartEnabled,
  isImageLoading,
  isCreateDatasetLoading,
  isDataIngestionTabEnabled,
  isNewDatasetReady,
  newDataset,
  userId,
  navigationClusterLoader = false,
  isExportEntitiesEnabled,
  isEnrichmentTabEnabled,
  savedViewsEnabled,
  showExportModal,
  sharingEnabled,
}: any) => {
  const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [datasetStatus, setDatasetStatus] = useState<DatasetStatusEnum | null>(
    null
  );

  const exportButtonRef = useRef<HTMLDivElement>(null);
  const optionsBtnRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryParamsFilters = useAllQueryParamsFilters();
  const allSelectedFilters = useSelector((state: State) =>
    getAllSelectedFilters(state, queryParamsFilters)
  );
  const { newDatasetId, enrichDatasetId, datasetId, viewsDatasetId } =
    useParams();

  const isDataCreationPage = dynamicPage === DATASET_CREATION_PAGE;
  const dynamicIsCardListLoading = isDataCreationPage
    ? isCreateDatasetLoading
    : dynamicPage === DATA_PAGE
    ? isCardListLoading
    : navigationClusterLoader;
  const dynamicDataset = newDataset || dataset;

  useEffect(() => {
    if (enrichDatasetId) {
      dispatch(setEnrichedDatasetId(enrichDatasetId));
    }
  }, [enrichDatasetId, dispatch]);

  const showDynamicTabs = (): TabTypes[] => {
    let result: TabTypes[] = [];
    //only show tabs after datasetStatus has loaded
    if (datasetStatus) {
      let isReady = [
        DatasetStatusEnum.READY,
        DatasetStatusEnum.ENRICHING,
      ].includes(datasetStatus as any);

      if (isReady || datasetId) {
        result.push(TabTypes.DATA_EXPLORATION);
      }
      if (isDataIngestionTabEnabled || newDatasetId) {
        result.push(TabTypes.DATA_INGESTION);
      }

      if ((isEnrichmentTabEnabled || enrichDatasetId) && isReady) {
        result.push(TabTypes.DATA_ENRICHMENT);
      }

      if (savedViewsEnabled && isReady) {
        result.push(TabTypes.SAVED_VIEWS);
      }
    }
    return result;
  };
  const isPublicDataset = dynamicDataset?.sample === "DEFAULT_SAMPLE";

  const showPublicHeader = isPublicDataset && !isContentLoading;
  const showCartButton = cartEnabled && !isDataCreationPage;
  const showDescription = !showPublicHeader && !!dynamicDataset;
  const showDatasetStatus = !showPublicHeader && !!datasetStatus;
  const showAbortButton =
    isDataCreationPage && !showPublicHeader && !isNewDatasetReady;

  const containerHeaderDetailClassnames = classNames(
    styles.containerHeaderDetails,
    {
      [styles.justifySpaceBetween]: isPublicDataset,
    }
  );

  const optionsButtonClassnames = classNames(styles.optionsButton, {
    [styles.selectedOptionsButton]: showOptionsDropdown,
  });

  const toggleOptionsDropdown = () => {
    setShowOptionsDropdown(!showOptionsDropdown);
  };

  const handleAbortClick = () => {
    dispatch(setShowDeleteDatasetModal(true));
  };

  const handleShareBtnClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isAnonymousUser()) {
      return;
    }
    event.stopPropagation();
    dispatch(setShowSharingMenu(true));
  };

  const handleSignInClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    userLogout();
  };

  const handleDeleteOptionClick = () => {
    setShowDeleteModal(true);
  };

  const handleNavigattionOnDelete = () => {
    navigate(PRIVATE_DATASETS_PATH);
  };

  const handleCopyURLClick = () => {
    let urlToCopy = new URL(window.location.href);
    copyToClipboard(urlToCopy.toString());

    toast(
      <div className={styles.toastNotification}>
        <CheckIcon />
        <span>Copied to clipboard!</span>
      </div>,

      {
        position: "bottom-center",
        closeButton: false,
      }
    );
  };

  const renderEditableTitle = () => {
    const isCreator = dynamicDataset?.created_by === userId;
    const isEditable = isOnPremUser() || isCreator;

    return isEditable ? (
      <EditableDatasetTitle
        title={dynamicDataset?.display_name || ""}
        datasetID={dynamicDataset?.id || ""}
        isContentLoading={isContentLoading}
        fontStyle={{ width: "310px" }}
      />
    ) : (
      <span
        className={styles.containerHeaderTitleText}
        title={dynamicDataset?.display_name}
      >
        {dynamicDataset?.display_name}
      </span>
    );
  };

  const renderShareBtn = () => {
    if (!sharingEnabled || newDatasetId) {
      return <noscript />;
    }
    const shareBtn = (
      <div
        data-tooltip-content="Share"
        data-tooltip-delay-show={500}
        onClick={handleShareBtnClick}
        className={styles.actionButton}
        data-tooltip-id={SHARE_BUTTON_TOOLTIP_ID}
      >
        <ShareIcon />
        <TooltipWrapper id={SHARE_BUTTON_TOOLTIP_ID} />
      </div>
    );
    if (!isAnonymousUser()) {
      return shareBtn;
    } else {
      return (
        <div data-tooltip-id="share-btn-tooltip" data-tooltip-place="bottom">
          {shareBtn}
          <TooltipWrapper
            id="share-btn-tooltip"
            isClickable={true}
            customClass="shareTooltip"
          >
            <div className={styles.shareTooltip}>
              <div className={styles.tooltipText}>
                Only signed-in users can share public Datasets. Please sign-up
                or sign-in to proceed
              </div>
              <div className={styles.signInBtn} onClick={handleSignInClick}>
                Sign In/Sign Up
              </div>
            </div>
          </TooltipWrapper>
        </div>
      );
    }
  };

  const renderExportBtn = () => {
    if (!isExportEntitiesEnabled || newDatasetId) {
      return <noscript />;
    }

    const handleExportClick = () => {
      dispatch(setShowExportModal(!showExportModal));
    };

    return (
      <>
        <div
          ref={exportButtonRef}
          onClick={handleExportClick}
          data-tooltip-content="Export"
          data-tooltip-delay-show={500}
          className={styles.actionButton}
          data-tooltip-id={EXPORT_BUTTON_TOOLTIP_ID}
        >
          <ExportIcon />
          {!showExportModal && <TooltipWrapper id={EXPORT_BUTTON_TOOLTIP_ID} />}
        </div>
        <div className={styles.exportModalPositioner}>
          {!showCart && (
            <ExportModal
              showModal={showExportModal}
              openedFrom="CONTAINER_HEADER"
              callerButtonRef={exportButtonRef}
            />
          )}
        </div>
      </>
    );
  };

  const renderOptionsBtn = () => {
    if (newDatasetId || !dynamicDataset) {
      return <noscript />;
    }

    return (
      <div
        ref={optionsBtnRef}
        data-tooltip-delay-show={500}
        onClick={toggleOptionsDropdown}
        className={optionsButtonClassnames}
        data-tooltip-content="More actions"
        data-tooltip-id={OPTIONS_BUTTON_TOOLTIP_ID}
      >
        <KebabIcon />
        {!showOptionsDropdown && (
          <TooltipWrapper id={OPTIONS_BUTTON_TOOLTIP_ID} />
        )}

        {showOptionsDropdown && (
          <div className={styles.actionOptionsPositioner}>
            <ActionOptions
              callerRef={optionsBtnRef}
              show={showOptionsDropdown}
              onToggle={toggleOptionsDropdown}
              onCopyClick={handleCopyURLClick}
              showDeleteOption={!isAnonymousUser()}
              onDeleteClick={handleDeleteOptionClick}
            />
          </div>
        )}

        <DatasetDeletionModal
          datasetId={dataset?.id}
          showModal={showDeleteModal}
          setShowModal={setShowDeleteModal}
          datasetName={dataset?.display_name}
          datasetSource={dataset?.source_type}
          handleNavigate={handleNavigattionOnDelete}
        />
      </div>
    );
  };

  const creationStatus = useSelector(getCreationStatus);

  useEffect(() => {
    let status: DatasetStatusEnum | null = null;

    if (datasetId || enrichDatasetId || viewsDatasetId) {
      status = dataset?.status || null;
    } else if (newDatasetId) {
      status = newDataset?.status || null;
    }

    if (creationStatus?.secondaryStatus?.length) {
      const visible = getVisibleSubtasks(creationStatus.secondaryStatus);
      const lastInProgressIndex = visible.findIndex((s) => s.progress < 100);
      if (lastInProgressIndex !== -1) {
        status = visible[lastInProgressIndex].status as DatasetStatusEnum;
      } else {
        status = DatasetStatusEnum.READY;
      }
    }
    setDatasetStatus(status);
  }, [
    datasetId,
    enrichDatasetId,
    newDatasetId,
    viewsDatasetId,
    newDataset,
    dataset,
    creationStatus,
  ]);

  return (
    <div className={styles.containerHeader}>
      <div className={styles.upperRow}>
        <div className={styles.containerLeft}>
          <div className={containerHeaderDetailClassnames}>
            <div className={styles.containerHeaderTitleContainer}>
              {renderEditableTitle()}
            </div>

            {showDescription && (
              <DescriptionList
                page={page}
                dataset={dynamicDataset}
                singleImageData={singleImageData}
                isContentLoading={isContentLoading}
              />
            )}
          </div>
        </div>

        {showPublicHeader && <PublicHeaderComponent />}
        {showDatasetStatus && (
          <DatasetStatusComponent datasetStatus={datasetStatus} />
        )}

        <div className={styles.actionButtons}>
          {showAbortButton && (
            <div className={styles.abortButton} onClick={handleAbortClick}>
              Abort
            </div>
          )}

          {showCartButton && (
            <SelectionBox
              exportCount={exportCount}
              showCart={showCart}
              clusters={clusters}
              navigationCluster={navigationCluster}
              exportData={exportData}
              allSelectedFilters={allSelectedFilters}
            />
          )}
          {showCartButton && <div className={styles.actionsDivider}></div>}
          <div className={styles.buttonGroup}>
            {renderShareBtn()}
            {renderExportBtn()}
            {renderOptionsBtn()}
          </div>
        </div>
      </div>

      <div className={styles.lowerRow}>
        <ShowLoader
          dynamicPage={dynamicPage}
          isImageLoading={isImageLoading}
          isCardListLoading={dynamicIsCardListLoading}
        />
        <TabSwitch showTabs={showDynamicTabs()} dataset={dataset} />
      </div>
    </div>
  );
};

const mapStateToProps = (state: State) => {
  return {
    dataset: getDataset(state),
    exportCount: getExportCount(state),
    exportData: getExportDataArray(state),
    showCart: getShowCart(state),
    clusters: getFilteredSimilarityClusters(state),
    navigationCluster: getNavigationCluster(state),
    cartEnabled: isCartEnabled(state),
    isImageLoading: getImageDataLoader(state),
    isDataIngestionTabEnabled: getIsDataIngestionTabEnabled(state),
    isCreateDatasetLoading: getIsLoading(state),
    newDatasetStatus: getNewDatasetStatus(state),
    isNewDatasetReady: getIsNewDatasetReady(state),
    newDataset: getNewDataset(state),
    userId: getUserInfo(state).userId,
    isExportEntitiesEnabled: getIsExportEntitiesEnabled(state),
    isEnrichmentTabEnabled: getIsEnrichmentTabEnabled(state),
    savedViewsEnabled: isSavedViewsEnabled(state),
    showExportModal: getShowExportModal(state),
    sharingEnabled: isSharingEnabled(state),
  };
};

export default connect(mapStateToProps)(ContainerHeader);
